/**
 * Base API Service
 * Handles HTTP requests with token injection and error handling
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5020/api/v1';

class ApiService {
  constructor() {
    this.refreshPromise = null; // Store ongoing refresh promise to prevent multiple refresh calls
  }

  /**
   * Get auth token from localStorage
   */
  getToken() {
    return localStorage.getItem('accessToken');
  }

  /**
   * Set auth token to localStorage with timestamp
   */
  setToken(token) {
    localStorage.setItem('accessToken', token);
    localStorage.setItem('tokenSetAt', Date.now().toString());
  }

  /**
   * Get refresh token from localStorage
   */
  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  /**
   * Set refresh token to localStorage
   */
  setRefreshToken(token) {
    localStorage.setItem('refreshToken', token);
  }

  /**
   * Remove tokens from localStorage
   */
  removeTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('tokenSetAt');
    this.refreshPromise = null;
  }

  /**
   * Get user data from localStorage
   */
  getUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  /**
   * Set user data to localStorage
   */
  setUser(user) {
    localStorage.setItem('user', JSON.stringify(user));
  }

  /**
   * Check if token is likely expired (based on time)
   * Access tokens typically expire in 15-60 minutes
   */
  isTokenLikelyExpired() {
    const tokenSetAt = localStorage.getItem('tokenSetAt');
    if (!tokenSetAt) return true;

    const now = Date.now();
    const tokenAge = now - parseInt(tokenSetAt, 10);

    // If token is older than 50 minutes, consider it potentially expired
    // The server will return 401 if actually expired, triggering a refresh
    return tokenAge > 50 * 60 * 1000;
  }

  /**
   * Build request headers
   */
  getHeaders(includeAuth = true, isFormData = false) {
    const headers = {};

    // Don't set Content-Type for FormData - browser will set it with boundary
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    if (includeAuth) {
      const token = this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken() {
    // If a refresh is already in progress, return that promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.removeTokens();
      window.location.href = '/login';
      throw new Error('No refresh token available');
    }

    try {
      // Store the refresh promise
      this.refreshPromise = fetch(`${API_BASE_URL}/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      })
        .then(async (response) => {
          const data = await response.json();

          if (!response.ok || !data.success) {
            throw new Error('Token refresh failed');
          }

          // The response structure is: { success: true, data: { tokens: { accessToken, refreshToken }, message } }
          const tokens = data.data?.tokens || data.tokens || data.data;

          // Update access token
          if (tokens.accessToken) {
            this.setToken(tokens.accessToken);
          }

          // Update refresh token if provided
          if (tokens.refreshToken) {
            this.setRefreshToken(tokens.refreshToken);
          }

          return tokens.accessToken;
        });

      const result = await this.refreshPromise;
      this.refreshPromise = null; // Clear the promise after success
      return result;
    } catch (error) {
      this.refreshPromise = null; // Clear the promise after failure

      // If refresh fails, log out user
      this.removeTokens();

      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }

      throw error;
    }
  }

  /**
   * Handle API errors
   */
  handleError(error, response) {
    if (response) {
      // Server responded with an error
      const errorData = response;
      const details = errorData.error?.details;

      // Prefer specific field-level validation messages when the server provides them,
      // otherwise fall back to the generic message.
      let message;
      if (Array.isArray(details) && details.length > 0) {
        message = details.map((d) => d.message).filter(Boolean).join('\n');
      }
      message =
        message || errorData.error?.message || errorData.message || 'An error occurred';

      const err = new Error(message);
      err.details = details; // keep structured errors for callers that want field-level info
      err.status = errorData.status;
      throw err;
    } else {
      // Network error or other issues
      throw new Error(error.message || 'Network error');
    }
  }

  /**
   * Make HTTP request with automatic token refresh
   */
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;

    // Detect if body is FormData
    const isFormData = options.body instanceof FormData;

    const config = {
      ...options,
      headers: {
        ...this.getHeaders(options.includeAuth !== false, isFormData),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      // Handle 401 Unauthorized - try to refresh token
      if (response.status === 401 && options.includeAuth !== false && !options._isRetry) {
        // Don't retry refresh token endpoint or login
        if (endpoint.includes('/auth/refresh-token') ||
            endpoint.includes('/auth/login') ||
            endpoint.includes('/auth/register')) {
          this.handleError(new Error('Request failed'), { ...data, status: response.status });
          return;
        }

        // Try to refresh token
        try {
          await this.refreshAccessToken();

          // Retry original request with new token
          return this.request(endpoint, { ...options, _isRetry: true });
        } catch (refreshError) {
          // Refresh failed, user will be redirected by refreshAccessToken
          throw refreshError;
        }
      }

      if (!response.ok) {
        this.handleError(new Error('Request failed'), { ...data, status: response.status });
      }

      return data;
    } catch (error) {
      if (error.message && error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw error;
    }
  }

  /**
   * GET request
   */
  async get(endpoint, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'GET',
    });
  }

  /**
   * POST request
   */
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * PATCH request
   */
  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'DELETE',
    });
  }
}

const apiService = new ApiService();
export default apiService;
